import { useEffect, useMemo, useState } from 'react'
import AdminShell from './AdminShell'
import {
  AdminButton,
  AdminCard,
  AdminField,
  AdminInput,
  AdminModal,
  AdminSkeleton,
  AdminStatePanel,
  AdminStatusPill,
  AdminToastRegion,
  type AdminToast,
} from './AdminUi'
import '../styles/admin-addons.css'

type SessionPayload = {
  authenticated: true
  user: { email: string; role: string }
  csrfToken: string
}

type AddOn = {
  id: string
  name: string
  packLabel: string
  shortDescription: string
  priceCents: number
  imageUrl: string | null
  stockPacks: number
  reservedPacks: number
  orderablePacks: number
  maxQuantityPerOrder: number
  enabled: boolean
  version: number
  createdAt: string
  updatedAt: string
}

type Draft = {
  name: string
  packLabel: string
  shortDescription: string
  price: string
  imageUrl: string
  stockPacks: string
  maxQuantityPerOrder: string
  enabled: boolean
}

const NZD = new Intl.NumberFormat('en-NZ', {
  style: 'currency',
  currency: 'NZD',
  minimumFractionDigits: 2,
})

function environmentFromHostname(hostname: string) {
  const host = hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}

function emptyDraft(): Draft {
  return {
    name: '',
    packLabel: '',
    shortDescription: '',
    price: '',
    imageUrl: '',
    stockPacks: '0',
    maxQuantityPerOrder: '3',
    enabled: false,
  }
}

function draftFromAddOn(item: AddOn): Draft {
  return {
    name: item.name,
    packLabel: item.packLabel,
    shortDescription: item.shortDescription,
    price: (item.priceCents / 100).toFixed(2),
    imageUrl: item.imageUrl ?? '',
    stockPacks: String(item.stockPacks),
    maxQuantityPerOrder: String(item.maxQuantityPerOrder),
    enabled: item.enabled,
  }
}

function centsFromPrice(value: string) {
  const number = Number(value)
  return Number.isFinite(number) ? Math.round(number * 100) : Number.NaN
}

export default function AdminAddOnsPage() {
  const [session, setSession] = useState<SessionPayload | null>(null)
  const [addOns, setAddOns] = useState<AddOn[] | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<AddOn | null>(null)
  const [draft, setDraft] = useState<Draft>(() => emptyDraft())
  const [toasts, setToasts] = useState<AdminToast[]>([])
  const environmentLabel = useMemo(() => environmentFromHostname(window.location.hostname), [])

  function toast(title: string, message?: string) {
    const id = crypto.randomUUID()
    setToasts([{ id, title, message, tone: 'success' }])
    window.setTimeout(() => setToasts((current) => current.filter((item) => item.id !== id)), 4_000)
  }

  async function load() {
    setBusy(true)
    setError('')
    try {
      const [sessionResponse, addOnsResponse] = await Promise.all([
        fetch('/api/admin/session', { credentials: 'same-origin', cache: 'no-store' }),
        fetch('/api/admin/addons', { credentials: 'same-origin', cache: 'no-store' }),
      ])
      if (sessionResponse.status === 401 || addOnsResponse.status === 401) {
        window.location.replace('/admin/login?next=/admin/add-ons')
        return
      }
      if (!sessionResponse.ok || !addOnsResponse.ok) throw new Error('Add-ons could not be loaded.')
      setSession(await sessionResponse.json() as SessionPayload)
      const payload = await addOnsResponse.json() as { addOns: AddOn[] }
      setAddOns(Array.isArray(payload.addOns) ? payload.addOns : [])
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Add-ons could not be loaded.')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => { void load() }, [])

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true)
    try {
      const response = await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': session.csrfToken },
      })
      if (!response.ok) throw new Error('Logout failed.')
      window.location.replace('/admin/login')
    } catch {
      setError('Logout could not be completed.')
      setLogoutBusy(false)
    }
  }

  function openCreate() {
    setEditing(null)
    setDraft(emptyDraft())
    setError('')
    setEditorOpen(true)
  }

  function openEdit(item: AddOn) {
    setEditing(item)
    setDraft(draftFromAddOn(item))
    setError('')
    setEditorOpen(true)
  }

  function validate() {
    if (!draft.name.trim()) return 'Name is required.'
    if (!draft.packLabel.trim()) return 'Pack label is required.'
    if (!draft.shortDescription.trim()) return 'Short description is required.'
    const priceCents = centsFromPrice(draft.price)
    if (!Number.isInteger(priceCents) || priceCents < 1) return 'Enter a valid price.'
    const stock = Number(draft.stockPacks)
    if (!Number.isInteger(stock) || stock < 0) return 'Stock must be a whole number of packs.'
    const max = Number(draft.maxQuantityPerOrder)
    if (!Number.isInteger(max) || max < 1 || max > 20) return 'Maximum per order must be between 1 and 20.'
    if (editing && stock < editing.reservedPacks) {
      return `Stock cannot be reduced below ${editing.reservedPacks} pack(s) currently reserved.`
    }
    return ''
  }

  async function save() {
    if (!session || busy) return
    const message = validate()
    if (message) {
      setError(message)
      return
    }

    setBusy(true)
    setError('')
    const body = {
      name: draft.name.trim(),
      packLabel: draft.packLabel.trim(),
      shortDescription: draft.shortDescription.trim(),
      priceCents: centsFromPrice(draft.price),
      imageUrl: draft.imageUrl.trim() || null,
      stockPacks: Number(draft.stockPacks),
      maxQuantityPerOrder: Number(draft.maxQuantityPerOrder),
      enabled: draft.enabled,
      ...(editing ? { expectedVersion: editing.version } : {}),
    }

    try {
      const response = await fetch(editing ? `/api/admin/addons/${encodeURIComponent(editing.id)}` : '/api/admin/addons', {
        method: editing ? 'PUT' : 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': session.csrfToken,
        },
        body: JSON.stringify(body),
      })
      const payload = await response.json().catch(() => null) as { message?: string } | null
      if (response.status === 401) {
        window.location.replace('/admin/login?next=/admin/add-ons')
        return
      }
      if (!response.ok) throw new Error(payload?.message || 'Add-on could not be saved.')
      setEditorOpen(false)
      setEditing(null)
      toast(editing ? 'Add-on updated' : 'Add-on created')
      await load()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Add-on could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  if (error && (!session || !addOns)) {
    return (
      <main className="admin-shell-gate-error">
        <AdminStatePanel state="error" title="Add-ons unavailable" description={error} action={<AdminButton onClick={() => void load()}>Retry</AdminButton>} />
      </main>
    )
  }

  if (!session || !addOns) {
    return <main className="admin-shell-gate-loading"><AdminCard><AdminSkeleton lines={7} /></AdminCard></main>
  }

  return (
    <AdminShell
      title="Add-ons"
      eyebrow="OPERATIONS"
      description="Manage optional extras customers can add to an existing box order."
      user={session.user}
      onLogout={() => void logout()}
      logoutBusy={logoutBusy}
      environmentLabel={environmentLabel}
      actions={<AdminButton type="button" onClick={openCreate}>Add Add-on</AdminButton>}
    >
      <div className="admin-addons-page">
        {error ? <div className="admin-addons__error" role="alert">{error}</div> : null}

        <AdminCard className="admin-addons__intro">
          <div>
            <span>ADD-ONS V1</span>
            <h2>Small extras. Separate inventory.</h2>
            <p>Stock is tracked in sellable packs. Add-ons never count as a Build Your Box slot and customers must have at least one normal box.</p>
          </div>
        </AdminCard>

        {addOns.length ? (
          <div className="admin-addons__grid">
            {addOns.map((item) => (
              <AdminCard className="admin-addons__card" key={item.id}>
                <div className="admin-addons__card-top">
                  <div>
                    <h2>{item.name}</h2>
                    <p>{item.packLabel}</p>
                  </div>
                  <AdminStatusPill tone={item.enabled && item.orderablePacks > 0 ? 'success' : 'neutral'}>
                    {item.enabled ? (item.orderablePacks > 0 ? 'Enabled' : 'Out of stock') : 'Disabled'}
                  </AdminStatusPill>
                </div>
                <p className="admin-addons__description">{item.shortDescription}</p>
                <dl className="admin-addons__stats">
                  <div><dt>Price</dt><dd>{NZD.format(item.priceCents / 100)}</dd></div>
                  <div><dt>Stock</dt><dd>{item.stockPacks} packs</dd></div>
                  <div><dt>Held</dt><dd>{item.reservedPacks} packs</dd></div>
                  <div><dt>Orderable now</dt><dd>{item.orderablePacks} packs</dd></div>
                  <div><dt>Maximum / order</dt><dd>{item.maxQuantityPerOrder}</dd></div>
                </dl>
                <AdminButton variant="secondary" type="button" onClick={() => openEdit(item)}>Edit</AdminButton>
              </AdminCard>
            ))}
          </div>
        ) : (
          <AdminCard>
            <AdminStatePanel
              state="empty"
              title="No add-ons yet"
              description="Create an add-on when you are ready. Nothing is shown to customers until it is enabled and has stock."
              action={<AdminButton type="button" onClick={openCreate}>Add Add-on</AdminButton>}
            />
          </AdminCard>
        )}

        <AdminModal
          open={editorOpen}
          title={editing ? 'Edit add-on' : 'Add Add-on'}
          description="Stock is entered in sellable packs, not individual pieces."
          onClose={() => { if (!busy) setEditorOpen(false) }}
          footer={(
            <>
              <AdminButton variant="secondary" type="button" onClick={() => setEditorOpen(false)} disabled={busy}>Cancel</AdminButton>
              <AdminButton type="button" onClick={() => void save()} disabled={busy}>{busy ? 'Saving…' : 'Save Add-on'}</AdminButton>
            </>
          )}
        >
          <div className="admin-addons__editor">
            <div className="admin-addons__editor-grid">
              <AdminField label="Name" required>
                <AdminInput
                  value={draft.name}
                  maxLength={80}
                  placeholder="Brownie Bombs"
                  onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                />
              </AdminField>
              <AdminField label="Pack label" required>
                <AdminInput
                  value={draft.packLabel}
                  maxLength={60}
                  placeholder="3 Pack"
                  onChange={(event) => setDraft((current) => ({ ...current, packLabel: event.target.value }))}
                />
              </AdminField>
            </div>

            <AdminField label="Short description" required>
              <textarea
                className="admin-ui-input admin-addons__textarea"
                value={draft.shortDescription}
                maxLength={240}
                rows={3}
                placeholder="A little extra brownie mischief."
                onChange={(event) => setDraft((current) => ({ ...current, shortDescription: event.target.value }))}
              />
            </AdminField>

            <div className="admin-addons__editor-grid admin-addons__editor-grid--three">
              <AdminField label="Price (NZD)" required>
                <AdminInput
                  inputMode="decimal"
                  value={draft.price}
                  placeholder="5.00"
                  onChange={(event) => setDraft((current) => ({ ...current, price: event.target.value }))}
                />
              </AdminField>
              <AdminField label="Stock available (packs)" required hint={editing?.reservedPacks ? `${editing.reservedPacks} currently held in active checkout(s).` : undefined}>
                <AdminInput
                  type="number"
                  min={0}
                  step={1}
                  value={draft.stockPacks}
                  onChange={(event) => setDraft((current) => ({ ...current, stockPacks: event.target.value }))}
                />
              </AdminField>
              <AdminField label="Maximum / order" required>
                <AdminInput
                  type="number"
                  min={1}
                  max={20}
                  step={1}
                  value={draft.maxQuantityPerOrder}
                  onChange={(event) => setDraft((current) => ({ ...current, maxQuantityPerOrder: event.target.value }))}
                />
              </AdminField>
            </div>

            <AdminField label="Image">
              <AdminInput
                value={draft.imageUrl}
                maxLength={2048}
                placeholder="/assets/... or https://..."
                onChange={(event) => setDraft((current) => ({ ...current, imageUrl: event.target.value }))}
              />
            </AdminField>

            <label className="admin-addons__enabled">
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(event) => setDraft((current) => ({ ...current, enabled: event.target.checked }))}
              />
              <span>
                <strong>Enabled for customers</strong>
                <small>It will only appear publicly while enabled and orderable stock remains.</small>
              </span>
            </label>
          </div>
        </AdminModal>

        <AdminToastRegion toasts={toasts} onDismiss={(id) => setToasts((current) => current.filter((item) => item.id !== id))} />
      </div>
    </AdminShell>
  )
}
